import { __rest, __assign, __spreadArray } from 'tslib';
import React, { forwardRef, useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { defaultAccessor, defaultFilterFn, processOptions } from './utils.js';
import { OptionsList } from './components/options-list/Component.js';
import { DEFAULT_VISIBLE_OPTIONS } from './consts.js';
import { Footer } from './presets/useSelectWithApply/options-list-with-apply/footer/Component.js';
import { Header } from './presets/useSelectWithApply/options-list-with-apply/header/Component.js';
import 'react-merge-refs';
import 'classnames';
import '../../mq/esm';
import '../../scrollbar/esm';
import '../../shared/esm';
import './components/optgroup/Component.js';
import '../../button/esm/desktop';
import '../../checkbox/esm';

var OptionsListWithApply = forwardRef(function (_a, ref) {
    var toggleMenu = _a.toggleMenu, defaultGetOptionProps = _a.getOptionProps, _b = _a.showClear, showClear = _b === void 0 ? true : _b, showHeaderWithSelectAll = _a.showHeaderWithSelectAll, _c = _a.selectedDraft, selectedDraft = _c === void 0 ? [] : _c, _d = _a.flatOptions, flatOptions = _d === void 0 ? [] : _d, _e = _a.OptionsList, OptionsList$1 = _e === void 0 ? OptionsList : _e, _f = _a.onApply, onApply = _f === void 0 ? function () { return null; } : _f, _g = _a.onClear, onClear = _g === void 0 ? function () { return null; } : _g, _h = _a.onClose, onClose = _h === void 0 ? function () { return null; } : _h, _j = _a.visibleOptions, visibleOptions = _j === void 0 ? DEFAULT_VISIBLE_OPTIONS : _j, _k = _a.Footer, Footer$1 = _k === void 0 ? Footer : _k, _l = _a.Header, Header$1 = _l === void 0 ? Header : _l, header = _a.header, headerProps = _a.headerProps, restProps = __rest(_a, ["toggleMenu", "getOptionProps", "showClear", "showHeaderWithSelectAll", "selectedDraft", "flatOptions", "OptionsList", "onApply", "onClear", "onClose", "visibleOptions", "Footer", "Header", "header", "headerProps"]);
    var getOptionProps = useCallback(function (option, index) {
        var optionProps = defaultGetOptionProps(option, index);
        var selected = option.key === SELECT_ALL_KEY
            ? selectedDraft.length === flatOptions.length - 1
            : selectedDraft.some(function (_a) {
                var key = _a.key;
                return key === option.key;
            });
        return __assign(__assign({}, optionProps), { selected: selected });
    }, [defaultGetOptionProps, flatOptions.length, selectedDraft]);
    var handleApply = useCallback(function () {
        onApply();
        toggleMenu();
    }, [onApply, toggleMenu]);
    var handleClear = useCallback(function () {
        onClear();
        toggleMenu();
    }, [onClear, toggleMenu]);
    useEffect(function () {
        var activeElement = document.activeElement;
        return function () {
            onClose();
            if (activeElement) {
                activeElement.focus();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var renderHeader = function () {
        if (!showHeaderWithSelectAll && !header)
            return undefined;
        return (React.createElement(React.Fragment, null,
            header,
            showHeaderWithSelectAll && React.createElement(Header$1, __assign({}, headerProps))));
    };
    return (React.createElement(OptionsList$1, __assign({}, restProps, { ref: ref, visibleOptions: visibleOptions, toggleMenu: toggleMenu, flatOptions: flatOptions, getOptionProps: getOptionProps, onApply: handleApply, onClear: handleClear, header: renderHeader(), footer: React.createElement(Footer$1, { handleApply: handleApply, handleClear: handleClear, showClear: showClear, selectedDraft: selectedDraft }) })));
});

var SELECT_ALL_KEY = 'select_all';
var selectAllOption = { key: SELECT_ALL_KEY, content: 'Выбрать все' };
function useSelectWithApply(_a) {
    var options = _a.options, selected = _a.selected, _b = _a.onChange, onChange = _b === void 0 ? function () { return null; } : _b, OptionsList = _a.OptionsList, _c = _a.optionsListProps, optionsListProps = _c === void 0 ? {} : _c, _d = _a.showClear, showClear = _d === void 0 ? true : _d, _e = _a.showSelectAll, showSelectAll = _e === void 0 ? false : _e, _f = _a.showHeaderWithSelectAll, showHeaderWithSelectAll = _f === void 0 ? false : _f, showSearch = _a.showSearch, _g = _a.searchProps, searchProps = _g === void 0 ? {} : _g;
    var _h = useState(''), searchState = _h[0], setSearchState = _h[1];
    var _j = typeof (searchProps === null || searchProps === void 0 ? void 0 : searchProps.value) === 'string'
        ? [searchProps.value, searchProps.onChange]
        : [searchState, setSearchState], search = _j[0], setSearch = _j[1];
    var accessor = searchProps.accessor || defaultAccessor;
    var filterFn = searchProps.filterFn || defaultFilterFn;
    var _k = useMemo(function () {
        return processOptions(options, selected, showSearch ? function (option) { return filterFn(accessor(option), search); } : undefined);
    }, [filterFn, accessor, options, search, selected, showSearch]), flatOptions = _k.flatOptions, selectedOptions = _k.selectedOptions;
    var _l = useState(selectedOptions), selectedDraft = _l[0], setSelectedDraft = _l[1];
    var selectedOptionsRef = useRef(selectedOptions);
    var handleApply = function () {
        onChange({
            selected: selectedDraft[0],
            selectedMultiple: selectedDraft,
            initiator: null,
        });
    };
    var handleClear = function () {
        setSelectedDraft([]);
        onChange({
            selected: null,
            selectedMultiple: [],
            initiator: null,
        });
    };
    var handleToggleAll = function () {
        setSelectedDraft(flatOptions.length === selectedDraft.length ? [] : flatOptions);
    };
    var handleChange = function (_a) {
        var initiator = _a.initiator, restArgs = __rest(_a, ["initiator"]);
        if (!initiator) {
            onChange(__assign({ initiator: null }, restArgs));
            return;
        }
        var initiatorSelected = selectedDraft.includes(initiator) ||
            (initiator.key === SELECT_ALL_KEY && selectedDraft.length === flatOptions.length);
        if (initiator.key === SELECT_ALL_KEY) {
            setSelectedDraft(initiatorSelected ? [] : flatOptions);
        }
        else {
            setSelectedDraft(initiatorSelected
                ? selectedDraft.filter(function (o) { return o !== initiator; })
                : selectedDraft.concat(initiator));
        }
    };
    var handleClose = function () { return setSelectedDraft(selectedOptionsRef.current); };
    useEffect(function () {
        setSelectedDraft(selectedOptions);
        selectedOptionsRef.current = selectedOptions;
    }, [selectedOptions]);
    var memoizedOptions = useMemo(function () { return (showSelectAll ? __spreadArray([selectAllOption], options, true) : options); }, [options, showSelectAll]);
    return {
        OptionsList: OptionsListWithApply,
        optionsListProps: __assign(__assign({}, optionsListProps), { OptionsList: OptionsList, showClear: showClear, onClear: handleClear, onApply: handleApply, onClose: handleClose, selectedDraft: selectedDraft, showHeaderWithSelectAll: showHeaderWithSelectAll, headerProps: {
                indeterminate: !!selectedDraft.length && selectedDraft.length < flatOptions.length,
                checked: selectedDraft.length === flatOptions.length,
                onChange: handleToggleAll,
            } }),
        allowUnselect: true,
        multiple: true,
        options: memoizedOptions,
        onChange: handleChange,
        selected: selected,
        showSearch: showSearch,
        searchProps: showSearch
            ? __assign(__assign({}, searchProps), { value: search, onChange: setSearch }) : undefined,
    };
}

export { OptionsListWithApply as O, SELECT_ALL_KEY as S, useSelectWithApply as u };
